import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import './DecryptedText.css';

const DEFAULT_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*()_+-=[]{}|;:,.<>?';

const DecryptedText = ({
  text = '',
  speed = 50,
  maxIterations = 10,
  sequential = true,
  revealDirection = 'start',
  useOriginalCharsOnly = false,
  characters = DEFAULT_CHARS,
  className = '',
  parentClassName = '',
  encryptedClassName = 'encrypted-char',
  animateOn = 'view', // 'view' | 'hover' | 'always'
  ...props
}) => {
  const [displayText, setDisplayText] = useState(text);
  const [isHovered, setIsHovered] = useState(false);
  const [isDecrypted, setIsDecrypted] = useState(false);
  const containerRef = useRef(null);
  const intervalRef = useRef(null);

  const getNextChar = (originalChar, charPool) => {
    if (originalChar === ' ') return ' ';
    if (useOriginalCharsOnly) {
      return originalChar;
    }
    const randomIndex = Math.floor(Math.random() * charPool.length);
    return charPool[randomIndex];
  };

  const startDecryptAnimation = () => {
    if (!text) return;
    let iteration = 0;
    const textLength = text.length;
    clearInterval(intervalRef.current);

    intervalRef.current = setInterval(() => {
      setDisplayText(prev => {
        return text
          .split('')
          .map((char, index) => {
            if (char === ' ') return ' ';

            let shouldReveal = false;
            if (sequential) {
              if (revealDirection === 'start') {
                shouldReveal = index < Math.floor(iteration / (maxIterations / textLength));
              } else if (revealDirection === 'end') {
                shouldReveal = index >= textLength - Math.floor(iteration / (maxIterations / textLength));
              } else if (revealDirection === 'center') {
                const mid = Math.floor(textLength / 2);
                const offset = Math.floor(iteration / (maxIterations / (textLength / 2)));
                shouldReveal = Math.abs(index - mid) <= offset;
              }
            } else {
              shouldReveal = iteration >= maxIterations;
            }

            if (shouldReveal) {
              return char;
            }

            return getNextChar(char, characters);
          })
          .join('');
      });

      iteration++;

      if (iteration > maxIterations * (sequential ? 1.5 : 1)) {
        clearInterval(intervalRef.current);
        setDisplayText(text);
        setIsDecrypted(true);
      }
    }, speed);
  };

  useEffect(() => {
    if (animateOn === 'always') {
      startDecryptAnimation();
      return () => clearInterval(intervalRef.current);
    }

    if (animateOn === 'view') {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && !isDecrypted) {
              startDecryptAnimation();
            }
          });
        },
        { threshold: 0.1 }
      );

      if (containerRef.current) {
        observer.observe(containerRef.current);
      }

      return () => {
        if (containerRef.current) observer.unobserve(containerRef.current);
        clearInterval(intervalRef.current);
      };
    }
  }, [text, animateOn]);

  const handleMouseEnter = () => {
    setIsHovered(true);
    if (animateOn === 'hover') {
      startDecryptAnimation();
    }
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  return (
    <motion.span
      ref={containerRef}
      className={`decrypted-text-container ${parentClassName}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      {...props}
    >
      {displayText.split('').map((char, index) => {
        const isOriginal = char === text[index];
        return (
          <span
            key={index}
            className={`${className} ${!isOriginal ? encryptedClassName : 'decrypted-char'}`}
          >
            {char}
          </span>
        );
      })}
    </motion.span>
  );
};

export default DecryptedText;
